import { useState, useEffect, useCallback, useRef } from "react";
import { useAuth } from "@/contexts/AuthContext";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { LoadingScreen } from "@/components/layout/LoadingScreen";
import { toast } from "sonner";
import { Shield, Download, History, Loader2, Clock, Trash2, RotateCcw, AlertTriangle } from "lucide-react";

export function BackupsTab({ onRefresh }) {
  const { authAxios } = useAuth();
  const authAxiosRef = useRef(authAxios);
  authAxiosRef.current = authAxios;
  const [snapshots, setSnapshots] = useState([]);
  const [loading, setLoading] = useState(true);
  const [creating, setCreating] = useState(false);
  const [restoring, setRestoring] = useState(null);
  const [confirmRestore, setConfirmRestore] = useState(null);

  const fetchSnapshots = useCallback(async () => {
    try {
      const res = await authAxiosRef.current.get("/menu/snapshots");
      setSnapshots(res.data);
    } catch { }
    setLoading(false);
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => { fetchSnapshots(); }, [fetchSnapshots]);

  const createBackup = async () => {
    setCreating(true);
    try {
      await authAxios.post("/menu/snapshots");
      toast.success("Backup creado con exito");
      fetchSnapshots();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Error creando backup");
    }
    setCreating(false);
  };

  const restoreBackup = async (id) => {
    setRestoring(id);
    try {
      const res = await authAxios.post(`/menu/snapshots/${id}/restore`);
      toast.success(res.data.message || "Menu restaurado con exito");
      setConfirmRestore(null);
      fetchSnapshots();
      onRefresh();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Error restaurando");
    }
    setRestoring(null);
  };

  const deleteBackup = async (id) => {
    try {
      await authAxios.delete(`/menu/snapshots/${id}`);
      toast.success("Backup eliminado");
      fetchSnapshots();
    } catch { toast.error("Error"); }
  };

  const triggerLabels = {
    manual: { label: "Manual", color: "bg-blue-100 text-blue-700" },
    auto_pre_reset: { label: "Pre-Reset", color: "bg-amber-100 text-amber-700" },
    auto_pre_restore: { label: "Pre-Restauracion", color: "bg-purple-100 text-purple-700" },
    auto_pre_template: { label: "Pre-Template", color: "bg-teal-100 text-teal-700" },
    auto_pre_clone: { label: "Pre-Clon", color: "bg-indigo-100 text-indigo-700" },
  };

  const formatDate = (iso) => {
    const d = new Date(iso);
    return d.toLocaleDateString("es-ES", { day: "2-digit", month: "2-digit", year: "numeric", hour: "2-digit", minute: "2-digit" });
  };

  const timeAgo = (iso) => {
    const diff = Date.now() - new Date(iso).getTime();
    const mins = Math.floor(diff / 60000);
    if (mins < 60) return `hace ${mins} min`;
    const hours = Math.floor(mins / 60);
    if (hours < 24) return `hace ${hours}h`;
    const days = Math.floor(hours / 24);
    return `hace ${days}d`;
  };

  if (loading) return <LoadingScreen />;

  return (
    <div data-testid="backups-tab" className="space-y-4">
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-3">
          <Shield className="w-5 h-5 text-emerald-600" />
          <div>
            <h3 className="font-bold text-gray-900">Backups del Cardapio</h3>
            <p className="text-xs text-gray-500">Snapshots automaticos y manuales. Restaure su menu con un clic.</p>
          </div>
        </div>
        <Button
          onClick={createBackup}
          disabled={creating}
          className="bg-emerald-600 hover:bg-emerald-700 text-white font-semibold"
          data-testid="create-backup-btn"
        >
          {creating ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : <Download className="w-4 h-4 mr-2" />}
          Crear Backup
        </Button>
      </div>

      {snapshots.length === 0 ? (
        <div className="bg-white rounded-lg border border-gray-200 p-12 text-center">
          <History className="w-12 h-12 text-gray-300 mx-auto mb-3" />
          <p className="text-gray-500 font-medium">Sin backups</p>
          <p className="text-sm text-gray-400 mt-1">Cree su primer backup manual o los automaticos se generaran al resetar o clonar el menu.</p>
        </div>
      ) : (
        <div className="space-y-2">
          {snapshots.map((snap) => {
            const trig = triggerLabels[snap.trigger] || { label: snap.trigger, color: "bg-gray-100 text-gray-600" };
            return (
              <div
                key={snap.id}
                className="bg-white rounded-lg border border-gray-200 p-4 flex items-center gap-4 hover:border-emerald-200 transition-colors"
                data-testid={`snapshot-${snap.id.slice(-6)}`}
              >
                <div className="flex-shrink-0 w-10 h-10 bg-emerald-50 rounded-full flex items-center justify-center">
                  <Clock className="w-5 h-5 text-emerald-600" />
                </div>
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2">
                    <span className="font-medium text-gray-900 text-sm">{formatDate(snap.created_at)}</span>
                    <Badge className={`text-[10px] px-1.5 py-0 ${trig.color}`}>{trig.label}</Badge>
                  </div>
                  <p className="text-xs text-gray-500 mt-0.5">
                    {snap.categories_count} categorias, {snap.products_count} productos &middot; {timeAgo(snap.created_at)}
                  </p>
                  {snap.description && <p className="text-xs text-gray-400 mt-0.5">{snap.description}</p>}
                </div>
                <div className="flex items-center gap-2 flex-shrink-0">
                  <Button
                    variant="outline"
                    size="sm"
                    className="text-emerald-700 border-emerald-300 hover:bg-emerald-50 font-semibold"
                    onClick={() => setConfirmRestore(snap)}
                    disabled={!!restoring}
                    data-testid={`restore-btn-${snap.id.slice(-6)}`}
                  >
                    {restoring === snap.id ? <Loader2 className="w-3.5 h-3.5 animate-spin mr-1" /> : <RotateCcw className="w-3.5 h-3.5 mr-1" />}
                    Restaurar
                  </Button>
                  <Button variant="ghost" size="icon" className="h-8 w-8 text-red-400 hover:text-red-600" onClick={() => deleteBackup(snap.id)}>
                    <Trash2 className="w-3.5 h-3.5" />
                  </Button>
                </div>
              </div>
            );
          })}
        </div>
      )}

      <Dialog open={!!confirmRestore} onOpenChange={() => setConfirmRestore(null)}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <AlertTriangle className="w-5 h-5 text-amber-500" />
              Restaurar Cardapio
            </DialogTitle>
          </DialogHeader>
          <div className="py-3 space-y-3">
            <p className="text-sm text-gray-600">
              Esto reemplazara todo su menu actual con el backup de:
            </p>
            {confirmRestore && (
              <div className="bg-gray-50 rounded-lg p-3 text-sm">
                <p className="font-medium">{formatDate(confirmRestore.created_at)}</p>
                <p className="text-gray-500">{confirmRestore.categories_count} categorias, {confirmRestore.products_count} productos</p>
              </div>
            )}
            <p className="text-xs text-emerald-600 flex items-center gap-1">
              <Shield className="w-3.5 h-3.5" />
              Se creara un backup automatico del menu actual antes de restaurar.
            </p>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setConfirmRestore(null)}>Cancelar</Button>
            <Button
              className="bg-emerald-600 hover:bg-emerald-700"
              onClick={() => confirmRestore && restoreBackup(confirmRestore.id)}
              disabled={!!restoring}
              data-testid="confirm-restore-btn"
            >
              {restoring ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : <RotateCcw className="w-4 h-4 mr-2" />}
              Confirmar Restauracion
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
